import React, { useContext, useMemo, useState } from 'react';

import FilterPlatesProvider from './FilterPlatesProvider';
import FilterPlatesContext from '../contexts/FilterPlatesContext';
import ReportGadgetContext from '../contexts/ReportGadgetContext';
import useDashboardGadgetDataTypes from '../hooks/useDashboardGadgetDataTypes';
import isReportGadgetDefaultFiltersDisabled from '../utils/isReportGadgetDefaultFiltersDisabled';

const ReportGadgetProvider = ({
  children,
  reportGadget,
  parentDrillDowns = [],
  setParentDrillDowns,
  parentScope = [],
  isParentDefaultFiltersDisabled = false,
}: {
  children: JSX.Element | JSX.Element[];
  reportGadget: ReportGadgets.ReportGadget;
  parentDrillDowns?: FilterPlateType[];
  setParentDrillDowns?: React.Dispatch<React.SetStateAction<FilterPlateType[]>>;
  parentScope?: FilterPlateType[];
  isParentDefaultFiltersDisabled?: boolean;
}) => {
  const { quickFilters: parentQuickFilters } = useContext(FilterPlatesContext);
  const dataTypes = useDashboardGadgetDataTypes(
    undefined,
    reportGadget.chartDef,
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const effectiveScope = useMemo(
    () =>
      reportGadget.isDefaultFiltersDisabled
        ? reportGadget.scope || window.emptyArray
        : [...parentScope, ...(reportGadget.scope || [])],
    [reportGadget.isDefaultFiltersDisabled, reportGadget.scope, parentScope],
  );

  const effectiveIsDefaultFiltersDisabled =
    isReportGadgetDefaultFiltersDisabled({
      defaultDatasetFiltersMode: reportGadget.defaultDatasetFiltersMode,
      isDefaultFiltersDisabled: reportGadget.isDefaultFiltersDisabled,
      isReportDefaultFiltersDisabled: isParentDefaultFiltersDisabled,
    });

  const contextValue = useMemo(
    () => ({
      reportGadget,
      isEditing,
      setIsEditing,
    }),
    [reportGadget, isEditing],
  );

  return (
    <ReportGadgetContext.Provider value={contextValue}>
      <FilterPlatesProvider
        drillDowns={
          reportGadget.isDefaultFiltersDisabled
            ? window.emptyArray
            : parentDrillDowns
        }
        setDrillDowns={
          reportGadget.isDefaultFiltersDisabled
            ? undefined
            : setParentDrillDowns
        }
        scope={effectiveScope}
        dataTypes={dataTypes}
        isDefaultFiltersDisabled={effectiveIsDefaultFiltersDisabled}
        quickFilters={parentQuickFilters}
      >
        {children}
      </FilterPlatesProvider>
    </ReportGadgetContext.Provider>
  );
};

export default ReportGadgetProvider;
